/**
 * NOTE: Only handles 1 popup at a time. Multiple popups need state management refactoring.
 */

/**
 * Base interface for dialog implementations
 */
class DialogStrategy {
	show(popup) {
		throw new Error('show() must be implemented');
	}

	close(popup) {
		throw new Error('close() must be implemented');
	}

	isOpen(popup) {
		throw new Error('isOpen() must be implemented');
	}

	setup(popup, manager) {
		// Optional setup for each strategy
	}

	cleanup(popup, manager) {
		// Optional cleanup for each strategy
	}
}

/**
 * Native HTML Dialog implementation
 */
class NativeDialogStrategy extends DialogStrategy {
	show(popup) {
		popup.showModal();
	}

	close(popup) {
		popup.close();
	}

	isOpen(popup) {
		return popup.open;
	}
}

/**
 * Fallback implementation for browsers without dialog support
 */
class FallbackDialogStrategy extends DialogStrategy {
	constructor() {
		super();
		this.firstFocusableElement = null;
		this.boundHandleKeydown = null;
		this.boundHandleClick = null;
		this.boundHandleFocusIn = null;
	}

	show(popup) {
		this.addFallbackOpenClass(popup);
		this.captureFirstFocusableElement(popup);
		this.addEventListeners(popup);
	}

	close(popup) {
		this.removeFallbackOpenClass(popup);
		this.removeEventListeners();
		popup.dispatchEvent(new Event('close'));
	}

	isOpen(popup) {
		return popup.classList.contains(PopupManager.CSS_CLASSES.FALLBACK_OPEN);
	}

	setup(popup, manager) {
		this.setAccessibilityAttributes(popup);
		this.manager = manager;
		this.popup = popup;
	}

	setAccessibilityAttributes(popup) {
		popup.setAttribute('role', 'dialog');
		popup.setAttribute('aria-modal', 'true');
	}

	addFallbackOpenClass(popup) {
		popup.classList.add(PopupManager.CSS_CLASSES.FALLBACK_OPEN);
	}

	removeFallbackOpenClass(popup) {
		popup.classList.remove(PopupManager.CSS_CLASSES.FALLBACK_OPEN);
	}

	captureFirstFocusableElement(popup) {
		const focusableSelectors = [
			'button:not([disabled])',
			'[href]',
			'input:not([disabled])',
			'select:not([disabled])',
			'textarea:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
		].join(', ');

		this.firstFocusableElement = popup.querySelector(focusableSelectors);
	}

	addEventListeners(popup) {
		this.boundHandleKeydown = (event) => this.handleKeydown(event);
		this.boundHandleClick = (event) => this.handleBackdropClick(event);
		this.boundHandleFocusIn = (event) => this.handleFocusIn(event);

		document.addEventListener('keydown', this.boundHandleKeydown);
		popup.addEventListener('click', this.boundHandleClick);
		window.addEventListener('focusin', this.boundHandleFocusIn);
	}

	removeEventListeners() {
		if (this.boundHandleKeydown) {
			document.removeEventListener('keydown', this.boundHandleKeydown);
		}
		if (this.boundHandleClick && this.popup) {
			this.popup.removeEventListener('click', this.boundHandleClick);
		}
		if (this.boundHandleFocusIn) {
			window.removeEventListener('focusin', this.boundHandleFocusIn);
		}
	}

	handleKeydown(event) {
		if (!this.isOpen(this.popup)) {
			return;
		}

		if (event.key === PopupManager.KEYS.ESCAPE && !this.manager.requiresAction()) {
			event.preventDefault();
			this.manager.closePopup();
		}
	}

	handleFocusIn(event) {
		if (!this.isOpen(this.popup)) {
			return;
		}

		if (!this.popup.contains(document.activeElement)) {
			this.returnFocusToPopup();
		}
	}

	returnFocusToPopup() {
		if (this.firstFocusableElement) {
			this.firstFocusableElement.focus();
		}
	}

	handleBackdropClick(event) {
		if (event.target === this.popup && !this.manager.requiresAction()) {
			this.manager.closePopup();
		}
	}

	cleanup() {
		this.removeEventListeners();
		this.firstFocusableElement = null;
		this.manager = null;
		this.popup = null;
	}
}

class PopupManager extends HTMLElement {
	constructor() {
		super();
		this.popup = null;
		this.accessibilityToggle = null;
		this.accessibilityToggleOriginalParent = null;
		this.accessibilityDrawer = null;
		this.accessibilityDrawerOriginalParent = null;
		this.dialogStrategy = null;
	}

	connectedCallback() {
		this.init();
	}

	init() {
		if (
			this.hasUserCompletedRequiredAction() ||
			this.shouldBypassPopupForCurrentPage() ||
			!this.shouldShowForViewport()
		) {
			return;
		}

		this.bindEvents();
		this.setupPopup();
	}

	hasUserCompletedRequiredAction() {
		const popupElement = this.getPopupElement();
		if (!popupElement) {
			return true;
		}

		const actionButton = popupElement.querySelector(PopupManager.SELECTORS.STORAGE_KEY_BUTTON);
		if (!actionButton) {
			return false;
		}

		return this.isStorageKeySetToTrue(actionButton.dataset[PopupManager.ATTRIBUTES.STORAGE_KEY]);
	}

	shouldBypassPopupForCurrentPage() {
		const popupElement = this.getPopupElement();
		if (!popupElement) {
			return false;
		}

		const allowedPages = popupElement.dataset[PopupManager.ATTRIBUTES.ALLOWED_PAGES];
		if (!allowedPages) {
			return false;
		}

		const currentPath = window.location.pathname;
		const allowedPagesList = allowedPages
			.split(PopupManager.SEPARATOR)
			.map((page) => page.trim())
			.filter((page) => page);

		return allowedPagesList.includes(currentPath);
	}

	getPopupElement() {
		return this.querySelector(PopupManager.SELECTORS.POPUP_BLOCK);
	}

	isStorageKeySetToTrue(storageKey) {
		try {
			return localStorage.getItem(storageKey) === 'true';
		} catch (error) {
			console.warn('Could not check popup completion status:', error);
			return false;
		}
	}

	shouldShowForViewport() {
		const popupElement = this.getPopupElement();
		if (!popupElement) {
			return false;
		}

		if (this.isAllDevicesPopup(popupElement)) {
			return true;
		}

		if (this.isViewportMismatch(popupElement)) {
			this.removePopupFromDOM();
			return false;
		}

		return true;
	}

	isAllDevicesPopup(popupElement) {
		const isMobileOnly = popupElement.classList.contains(PopupManager.CSS_CLASSES.MOBILE_ONLY);
		const isDesktopOnly = popupElement.classList.contains(PopupManager.CSS_CLASSES.DESKTOP_ONLY);
		return !isMobileOnly && !isDesktopOnly;
	}

	isViewportMismatch(popupElement) {
		const isMobileOnly = popupElement.classList.contains(PopupManager.CSS_CLASSES.MOBILE_ONLY);
		const isDesktopOnly = popupElement.classList.contains(PopupManager.CSS_CLASSES.DESKTOP_ONLY);
		const isMobileViewport = this.isMobileViewport();

		return (isMobileOnly && !isMobileViewport) || (isDesktopOnly && isMobileViewport);
	}

	isMobileViewport() {
		return window.matchMedia(PopupManager.MEDIA_QUERIES.MOBILE_BREAKPOINT).matches;
	}

	removePopupFromDOM() {
		const popupElement = this.getPopupElement();
		if (popupElement) {
			popupElement.remove();
		}
	}

	supportsNativeDialog() {
		return typeof HTMLDialogElement === 'function' && this.popup && typeof this.popup.showModal === 'function';
	}

	initializeDialogStrategy() {
		this.dialogStrategy = this.createDialogStrategy();
		this.dialogStrategy.setup(this.popup, this);
		this.bindPopupCloseEvent();
	}

	createDialogStrategy() {
		if (this.supportsNativeDialog()) {
			return new NativeDialogStrategy();
		}
		return new FallbackDialogStrategy();
	}

	bindEvents() {
		this.bindPageCacheEvents();
		this.bindClickEvents();
		this.bindKeyboardEvents();
	}

	bindPageCacheEvents() {
		window.addEventListener('pageshow', this.handlePageRestore.bind(this));
	}

	handlePageRestore(event) {
		if (!event.persisted) {
			return;
		}

		/**
		 * During testing, Safari's bfcache left loading/error states visible
		 * after navigating back from redirect. Reset universally to prevent
		 * similar issues across all browsers.
		 */
		this.resetPopupState();
	}

	resetPopupState() {
		const popup = this.getPopupElement();
		if (!popup) {
			return;
		}

		this.resetButtonsContainerState(popup);
		this.resetMessageContainerState(popup);
	}

	resetButtonsContainerState(popup) {
		const buttonsContainer = popup.querySelector(PopupManager.SELECTORS.BUTTONS_CONTAINER);
		if (!buttonsContainer) {
			return;
		}

		buttonsContainer.classList.remove(PopupManager.CSS_CLASSES.LOADING);
	}

	resetMessageContainerState(popup) {
		const messageContainer = popup.querySelector(PopupManager.SELECTORS.MESSAGE_CONTAINER);
		if (!messageContainer) {
			return;
		}

		messageContainer.classList.remove(PopupManager.CSS_CLASSES.MESSAGE_VISIBLE);
	}

	bindClickEvents() {
		this.addEventListener('click', (event) => {
			const button = event.target.closest(PopupManager.SELECTORS.POPUP_BUTTON);
			if (button) {
				const popup = event.target.closest(PopupManager.SELECTORS.POPUP_BLOCK);
				this.handleButtonAction(button, popup);
			}
		});
	}

	bindKeyboardEvents() {
		// Native dialog handles escape key automatically
		// Only prevent escape for required action popups
		document.addEventListener('keydown', (event) => {
			if (
				event.key === PopupManager.KEYS.ESCAPE &&
				this.dialogStrategy &&
				this.dialogStrategy.isOpen(this.popup) &&
				this.requiresAction()
			) {
				event.preventDefault();
			}
		});
	}

	bindPopupCloseEvent() {
		if (this.popup) {
			this.popup.addEventListener('close', () => {
				this.handleDialogClose();
			});
		}
	}

	handleDialogClose() {
		this.moveAccessibilityToggleBack();
		this.moveAccessibilityDrawerBack();
	}

	requiresAction() {
		return this.popup.dataset[PopupManager.ATTRIBUTES.REQUIRE_ACTION] === 'true';
	}

	setupPopup() {
		const popupElement = this.getPopupElement();
		if (!popupElement) {
			return;
		}

		const delayInMs = this.getDelayInMilliseconds(popupElement);
		this.popup = popupElement;

		this.initializeDialogStrategy();

		setTimeout(() => {
			this.showPopup();
		}, delayInMs);
	}

	getDelayInMilliseconds(popupElement) {
		const delayInSeconds = parseInt(popupElement.dataset[PopupManager.ATTRIBUTES.DELAY], 10) || 0;
		return delayInSeconds * PopupManager.TIMEOUTS.SECONDS_TO_MS;
	}

	showPopup() {
		if (!this.isPopupReadyToShow()) {
			return;
		}

		this.prepareAccessibilityElements();
		this.displayDialog();
		this.applyBodyScrollLockIfRequired();
		this.focusFirstButtonAfterDelay();
	}

	isPopupReadyToShow() {
		return this.popup && typeof this.dialogStrategy?.show === 'function';
	}

	prepareAccessibilityElements() {
		this.moveAccessibilityToggleToDialog();
		this.moveAccessibilityDrawerToDialog();
	}

	displayDialog() {
		this.dialogStrategy.show(this.popup);
	}

	applyBodyScrollLockIfRequired() {
		if (this.requiresAction()) {
			this.lockBodyScroll();
		}
	}

	focusFirstButtonAfterDelay() {
		const firstButton = this.popup.querySelector(PopupManager.SELECTORS.BUTTON);
		if (firstButton) {
			setTimeout(() => {
				firstButton.focus();
			}, PopupManager.TIMEOUTS.FOCUS_DELAY_MS);
		}
	}

	closePopup() {
		if (!this.isPopupReadyToClose()) {
			return;
		}

		this.removeBodyScrollLockIfRequired();
		this.hideDialog();
	}

	isPopupReadyToClose() {
		return this.popup && typeof this.dialogStrategy?.close === 'function';
	}

	removeBodyScrollLockIfRequired() {
		if (this.requiresAction()) {
			this.unlockBodyScroll();
		}
	}

	hideDialog() {
		this.dialogStrategy.close(this.popup);
	}

	moveAccessibilityToggleToDialog() {
		this.accessibilityToggle = document.querySelector(PopupManager.SELECTORS.ACCESSIBILITY_TOGGLE);
		if (!this.accessibilityToggle || !this.popup) {
			return;
		}

		this.accessibilityToggleOriginalParent = this.accessibilityToggle.parentNode;
		this.popup.appendChild(this.accessibilityToggle);
	}

	moveAccessibilityToggleBack() {
		if (this.accessibilityToggle && this.accessibilityToggleOriginalParent) {
			this.accessibilityToggleOriginalParent.appendChild(this.accessibilityToggle);
		}
	}

	moveAccessibilityDrawerToDialog() {
		this.accessibilityDrawer = document.querySelector(PopupManager.SELECTORS.ACCESSIBILITY_DRAWER);
		if (!this.accessibilityDrawer || !this.popup) {
			return;
		}

		this.accessibilityDrawerOriginalParent = this.accessibilityDrawer.parentNode;
		this.popup.appendChild(this.accessibilityDrawer);
	}

	moveAccessibilityDrawerBack() {
		if (this.accessibilityDrawer && this.accessibilityDrawerOriginalParent) {
			this.accessibilityDrawerOriginalParent.appendChild(this.accessibilityDrawer);
		}
	}

	handleButtonAction(button, popup) {
		const storageKey = button.dataset[PopupManager.ATTRIBUTES.STORAGE_KEY];
		const action = button.dataset[PopupManager.ATTRIBUTES.ACTION];
		const actionOptions = {
			popup,
			errorMessage: button.dataset[PopupManager.ATTRIBUTES.ERROR_MESSAGE],
			redirectUrl: button.dataset[PopupManager.ATTRIBUTES.REDIRECT_URL],
			redirectDelay: parseInt(button.dataset[PopupManager.ATTRIBUTES.REDIRECT_DELAY], 10) || 0,
		};

		if (storageKey) {
			this.storeCompletionState(storageKey);
		}
		this.executeAction(action, actionOptions);
	}

	storeCompletionState(storageKey) {
		try {
			localStorage.setItem(storageKey, 'true');
		} catch (error) {
			console.warn('Failed to store button action:', error);
		}
	}

	/**
	 * @param {string} action - Action type to execute
	 * @param {Object} options - Action execution options
	 * @param {HTMLElement} options.popup - Popup element
	 * @param {string} [options.errorMessage] - Optional error message to display
	 * @param {string} [options.redirectUrl] - Optional URL for redirect action
	 * @param {number} [options.redirectDelay] - Optional delay in seconds before redirect
	 */
	executeAction(action, options) {
		switch (action) {
			case PopupManager.ACTIONS.SHOW_MESSAGE: {
				this.handleShowMessageAction(options);
				break;
			}
			case PopupManager.ACTIONS.REDIRECT: {
				this.handleRedirectAction(options);
				break;
			}
			case PopupManager.ACTIONS.CLOSE: {
				this.closePopup();
				break;
			}
			default: {
				throw new Error('#PopupManager - executeAction(): Unknown action');
			}
		}
	}

	/**
	 * @param {Object} options - Action execution options
	 * @param {HTMLElement} options.popup - Popup element
	 * @param {string} options.errorMessage - Eror message to display
	 */
	handleShowMessageAction(options) {
		const { popup, errorMessage } = options;
		if (!errorMessage) {
			return;
		}

		this.showMessage(popup, errorMessage, PopupManager.MESSAGE_TYPES.ERROR);
	}

	/**
	 * @param {Object} options - Action execution options
	 * @param {HTMLElement} options.popup - Popup element
	 * @param {string} options.errorMessage - Optional error message to display
	 * @param {string} options.redirectUrl - Optional URL for redirect action
	 * @param {number} options.redirectDelay - Optional delay in seconds before redirect
	 */
	handleRedirectAction(options) {
		const { popup, errorMessage, redirectUrl, redirectDelay } = options;
		if (errorMessage) {
			this.showMessage(popup, errorMessage, PopupManager.MESSAGE_TYPES.ERROR);
		}

		this.showLoadingState(popup);

		if (redirectUrl) {
			this.handleRedirect(redirectUrl, redirectDelay);
		}
	}

	showLoadingState(popup) {
		const buttonsContainer = popup.querySelector(PopupManager.SELECTORS.BUTTONS_CONTAINER);
		if (!buttonsContainer) {
			return;
		}

		buttonsContainer.classList.add(PopupManager.CSS_CLASSES.LOADING);
	}

	handleRedirect(redirectUrl, redirectDelayInSeconds) {
		if (!redirectUrl) {
			return;
		}

		const delayInSeconds = redirectDelayInSeconds ?? PopupManager.TIMEOUTS.DEFAULT_REDIRECT_DELAY_SECONDS;
		const delayInMs = delayInSeconds * PopupManager.TIMEOUTS.SECONDS_TO_MS;

		setTimeout(() => {
			window.location.href = redirectUrl;
		}, delayInMs);
	}

	showMessage(popup, message, type = PopupManager.MESSAGE_TYPES.INFO) {
		const messageContainer = popup.querySelector(PopupManager.SELECTORS.MESSAGE_CONTAINER);
		const messageContent = popup.querySelector(PopupManager.SELECTORS.MESSAGE_CONTENT);

		if (!messageContainer || !messageContent) {
			return;
		}

		messageContent.textContent = message;
		messageContent.className = PopupManager.CSS_CLASSES.MESSAGE_CONTENT_BASE;

		if (type === PopupManager.MESSAGE_TYPES.ERROR) {
			this.applyErrorMessageStyles(messageContent, popup);
		}

		messageContainer.classList.add(PopupManager.CSS_CLASSES.MESSAGE_VISIBLE);
	}

	applyErrorMessageStyles(messageContent, popup) {
		messageContent.classList.add(PopupManager.CSS_CLASSES.MESSAGE_ERROR);

		if (popup.dataset[PopupManager.ATTRIBUTES.SHOW_ERROR_BACKGROUND] === 'true') {
			messageContent.classList.add(PopupManager.CSS_CLASSES.MESSAGE_ERROR_WITH_BG);
		}
	}

	lockBodyScroll() {
		document.body.classList.add(PopupManager.CSS_CLASSES.BODY_SCROLL_LOCK);
	}

	unlockBodyScroll() {
		document.body.classList.remove(PopupManager.CSS_CLASSES.BODY_SCROLL_LOCK);
	}
}

// Static properties - compatible with older browsers
PopupManager.SELECTORS = {
	POPUP_BLOCK: '[data-popup-block]',
	POPUP_BUTTON: '.popup__button',
	STORAGE_KEY_BUTTON: '[data-storage-key]',
	MESSAGE_CONTAINER: '.popup__message',
	MESSAGE_CONTENT: '.popup__message-content',
	BUTTONS_CONTAINER: '.popup__buttons',
	BUTTON: 'button',
	ACCESSIBILITY_TOGGLE: 'accessibility-toggle',
	ACCESSIBILITY_DRAWER: '#DrawerAccessibility',
};

PopupManager.ATTRIBUTES = {
	REQUIRE_ACTION: 'requireAction',
	DELAY: 'delay',
	ACTION: 'action',
	STORAGE_KEY: 'storageKey',
	ERROR_MESSAGE: 'errorMessage',
	ALLOWED_PAGES: 'allowedPages',
	SHOW_ERROR_BACKGROUND: 'showErrorBackground',
	REDIRECT_URL: 'redirectUrl',
	REDIRECT_DELAY: 'redirectDelay',
};

PopupManager.ACTIONS = {
	SHOW_MESSAGE: 'show_message',
	CLOSE: 'close',
	REDIRECT: 'redirect',
};

PopupManager.TIMEOUTS = {
	FOCUS_DELAY_MS: 100,
	MESSAGE_HIDE_DELAY_MS: 5000,
	SECONDS_TO_MS: 1000,
	DEFAULT_REDIRECT_DELAY_SECONDS: 2,
};

PopupManager.MESSAGE_TYPES = {
	INFO: 'info',
	ERROR: 'error',
};

PopupManager.KEYS = {
	ESCAPE: 'Escape',
};

PopupManager.CSS_CLASSES = {
	MESSAGE_CONTENT_BASE: 'popup__message-content',
	MESSAGE_ERROR: 'popup__message--error',
	MESSAGE_ERROR_WITH_BG: 'popup__message--error-with-bg',
	MESSAGE_VISIBLE: 'is-visible',
	MOBILE_ONLY: 'popup--mobile-only',
	DESKTOP_ONLY: 'popup--desktop-only',
	FALLBACK_OPEN: 'popup--fallback-open',
	BODY_SCROLL_LOCK: 'popup-scroll-lock',
	LOADING: 'loading',
};

PopupManager.MEDIA_QUERIES = {
	MOBILE_BREAKPOINT: '(max-width: 767px)',
};

PopupManager.SEPARATOR = '|';

if (!customElements.get('popup-manager')) {
	customElements.define('popup-manager', PopupManager);
}
